import { Link, useLocation, useParams, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';

import { api } from '@/lib/api';
import { chaveDoEstado, formatoDaUrl, type FormatoImpressao } from '@/features/marcacao/formato';
import type { RespostaMarcacao } from '@/features/marcacao/tipos';
import { ImpressaoMarcacao } from '@/components/marcacao/impressao-marcacao';
import { Botao } from '@/components/ui/button';

/**
 * Comprovante de marcação de uma solicitação do KAVI: o cupom de 80 mm, que é
 * o padrão, ou a folha A4 no layout do eSUS Regulação.
 *
 * Os dados vêm do eSUS na hora em que a tela abre, e não do que o KAVI gravou
 * ao agendar: uma remarcação feita lá depois precisa sair no papel.
 */
export function PaginaMarcacao() {
  const { id } = useParams<{ id: string }>();
  const [parametros, setParametros] = useSearchParams();
  const formato = formatoDaUrl(parametros.get('formato'));
  const local = useLocation();

  // `replace`: trocar o formato não é navegação, e o "voltar" do navegador
  // deve levar à solicitação, não ao formato anterior.
  const escolherFormato = (novo: FormatoImpressao) => {
    // O estado leva a chave de confirmação junto.
    setParametros({ formato: novo }, { replace: true, state: local.state });
  };

  const consulta = useQuery({
    queryKey: ['marcacao', id],
    queryFn: () => api.get<RespostaMarcacao>(`/solicitacoes/${id}/marcacao`),
    enabled: Boolean(id),
    // Cada abertura é uma consulta auditada ao eSUS: nada de repetir sozinha.
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  return (
    <ImpressaoMarcacao
      consulta={consulta}
      formato={formato}
      aoEscolherFormato={escolherFormato}
      chaveConfirmacao={chaveDoEstado(local.state)}
      voltar={
        <Botao variante="fantasma" tamanho="sm" asChild className="-ml-3">
          <Link to={`/solicitacoes/${id}`}>
            <ArrowLeft aria-hidden="true" />
            Voltar à solicitação
          </Link>
        </Botao>
      }
    />
  );
}
